import type { AdvancedFilterQuery } from '@packages/types';
import { useEffect, useRef } from 'react';
import type { FilterMetadata } from '../types/filter-metadata.types';
import { FilterInputType } from '../types/filter-metadata.types';
import type { ConditionOperator, FilterGroup, GroupOperator } from '../types/filter-panel.types';

interface UseSyncInitialFiltersProps {
    initialAdvanced?: AdvancedFilterQuery;
    metadata?: { filters: FilterMetadata[] };
    filterGroups: FilterGroup[];
    setFilterGroups: React.Dispatch<React.SetStateAction<FilterGroup[]>>;
    setMultiSelectOperator: (
        updater:
            | Record<string, GroupOperator>
            | ((prev: Record<string, GroupOperator>) => Record<string, GroupOperator>)
    ) => void;
    setMultiSelectInValues: (
        updater: Record<string, any[]> | ((prev: Record<string, any[]>) => Record<string, any[]>)
    ) => void;
    setMultiSelectOutValues: (
        updater: Record<string, any[]> | ((prev: Record<string, any[]>) => Record<string, any[]>)
    ) => void;
    setFieldLabels: (
        updater:
            | Record<string, Record<string, string>>
            | ((prev: Record<string, Record<string, string>>) => Record<string, Record<string, string>>)
    ) => void;
}

/**
 * Checks if a condition has an empty value
 */
const isEmptyCondition = (c: { value?: any }): boolean => {
    if (c.value === undefined || c.value === null || c.value === '') return true;
    if (Array.isArray(c.value) && c.value.length === 0) return true;
    return false;
};

/**
 * Merges initial filter groups with current empty conditions to preserve UI state
 */
const mergeGroupsPreservingEmptyConditions = (
    initialGroups: AdvancedFilterQuery['groups'],
    currentGroups: FilterGroup[]
): FilterGroup[] => {
    const newGroups = initialGroups.map((group, idx) => {
        const groupId = `${idx + 1}`;
        const currentGroup = currentGroups.find(g => g.id === groupId);
        const initialConditions = group.conditions.map((c, cIdx) => {
            const baseCondition: any = {
                id: `${idx + 1}-${cIdx + 1}`,
                field: c.field,
                operator: c.operator as ConditionOperator,
                value: c.value,
                conditionOperator: (c as any).conditionOperator as GroupOperator | undefined,
            };

            // Preserve metadata for note_type, procedure_type, and measurement_type
            if ((c as any).noteTypeFilters) {
                baseCondition.noteTypeFilters = (c as any).noteTypeFilters;
            }
            if ((c as any).procedureTypeFilters) {
                baseCondition.procedureTypeFilters = (c as any).procedureTypeFilters;
            }
            if ((c as any).measurementValueRanges) {
                baseCondition.measurementValueRanges = (c as any).measurementValueRanges;
            }
            // Preserve other metadata that might exist
            if ((c as any).logicalOperator) {
                baseCondition.logicalOperator = (c as any).logicalOperator;
            }
            if ((c as any).valueStatuses) {
                baseCondition.valueStatuses = (c as any).valueStatuses;
            }
            if ((c as any).valueTitleMap) {
                baseCondition.valueTitleMap = (c as any).valueTitleMap;
            }

            return baseCondition;
        });

        if (currentGroup) {
            // Find empty conditions in current group
            const emptyConditions = currentGroup.conditions.filter(isEmptyCondition);
            const initialConditionFields = new Set<string>(initialConditions.map(c => c.field));
            const additionalEmptyConditions = emptyConditions
                .filter(c => !initialConditionFields.has(c.field))
                .map((c, emptyIdx) => ({
                    ...c,
                    id: `${idx + 1}-${initialConditions.length + emptyIdx + 1}`,
                }));

            return {
                id: groupId,
                operator: (group.operator || 'AND') as GroupOperator,
                conditions: [...initialConditions, ...additionalEmptyConditions],
            };
        }

        return {
            id: groupId,
            operator: (group.operator || 'AND') as GroupOperator,
            conditions: initialConditions,
        };
    });

    // Preserve additional groups from current that don't exist in initial
    const initialGroupIds = new Set(newGroups.map(g => g.id));
    const additionalGroups = currentGroups
        .filter(g => !initialGroupIds.has(g.id))
        .map(g => ({
            ...g,
            conditions: g.conditions.filter(isEmptyCondition),
        }))
        .filter(g => g.conditions.length > 0);

    return [...newGroups, ...additionalGroups];
};

/**
 * Loads multi-select state from saved filter conditions
 */
const loadMultiSelectState = (
    initialAdvanced: AdvancedFilterQuery,
    metadata: FilterMetadata[]
): {
    operators: Record<string, GroupOperator>;
    inValues: Record<string, any[]>;
    outValues: Record<string, any[]>;
    labels: Record<string, Record<string, string>>;
} => {
    const operators: Record<string, GroupOperator> = {};
    const inValues: Record<string, any[]> = {};
    const outValues: Record<string, any[]> = {};
    const labels: Record<string, Record<string, string>> = {};

    initialAdvanced.groups.forEach((group, idx) => {
        const groupId = `${idx + 1}`;
        group.conditions.forEach((c, cIdx) => {
            const conditionId = `${idx + 1}-${cIdx + 1}`;
            const conditionKey = `${groupId}-${conditionId}`;

            const meta = metadata.find(f => f.field === c.field);
            const isMultiSelect = meta?.inputType === FilterInputType.MULTI_SELECT;

            if (isMultiSelect && Array.isArray(c.value) && c.value.length > 0) {
                // Load logical operator
                if ((c as any).logicalOperator) {
                    operators[conditionKey] = (c as any).logicalOperator as GroupOperator;
                }

                const valueTitleMap = (c as any).valueTitleMap || {};

                // Load IN/OUT values from valueStatuses
                if ((c as any).valueStatuses) {
                    const statuses = (c as any).valueStatuses;
                    const inVals: any[] = [];
                    const outVals: any[] = [];

                    c.value.forEach((val: any) => {
                        const title = valueTitleMap[String(val)] || String(val);
                        const item = { value: val, title };
                        if (statuses[String(val)] === 'OUT') {
                            outVals.push(item);
                        } else {
                            inVals.push(item);
                        }
                    });

                    if (inVals.length > 0) inValues[conditionKey] = inVals;
                    if (outVals.length > 0) outValues[conditionKey] = outVals;
                } else {
                    // Default all to IN
                    inValues[conditionKey] = c.value.map((val: any) => ({
                        value: val,
                        title: valueTitleMap[String(val)] || String(val),
                    }));
                }

                // Load value title map
                if ((c as any).valueTitleMap) {
                    labels[conditionKey] = (c as any).valueTitleMap as Record<string, string>;
                }
            }
        });
    });

    return { operators, inValues, outValues, labels };
};

/**
 * Hook to sync initial filter state and load multi-select data
 */
export const useSyncInitialFilters = ({
    initialAdvanced,
    metadata,
    filterGroups,
    setFilterGroups,
    setMultiSelectOperator,
    setMultiSelectInValues,
    setMultiSelectOutValues,
    setFieldLabels,
}: UseSyncInitialFiltersProps): void => {
    const previousInitialAdvancedRef = useRef<string>('');

    useEffect(() => {
        if (!initialAdvanced?.groups?.length) return;

        const currentSerialized = JSON.stringify(initialAdvanced);

        // Check if initialAdvanced is already reflected in current filterGroups
        // Include metadata in comparison to detect changes
        const currentGroupsSerialized = JSON.stringify(
            filterGroups.map(g => ({
                operator: g.operator,
                conditions: g.conditions.map(c => {
                    const base: any = {
                        field: c.field,
                        operator: c.operator,
                        value: c.value,
                    };
                    // Include metadata in comparison
                    if ((c as any).noteTypeFilters) base.noteTypeFilters = (c as any).noteTypeFilters;
                    if ((c as any).procedureTypeFilters) base.procedureTypeFilters = (c as any).procedureTypeFilters;
                    if ((c as any).measurementValueRanges)
                        base.measurementValueRanges = (c as any).measurementValueRanges;
                    return base;
                }),
            }))
        );

        const initialGroupsSerialized = JSON.stringify(
            initialAdvanced.groups.map(g => ({
                operator: g.operator || 'AND',
                conditions: g.conditions.map(c => {
                    const base: any = {
                        field: c.field,
                        operator: c.operator,
                        value: c.value,
                    };
                    // Include metadata in comparison
                    if ((c as any).noteTypeFilters) base.noteTypeFilters = (c as any).noteTypeFilters;
                    if ((c as any).procedureTypeFilters) base.procedureTypeFilters = (c as any).procedureTypeFilters;
                    if ((c as any).measurementValueRanges)
                        base.measurementValueRanges = (c as any).measurementValueRanges;
                    return base;
                }),
            }))
        );

        const shouldSkipGroupsUpdate =
            currentSerialized === previousInitialAdvancedRef.current ||
            currentGroupsSerialized === initialGroupsSerialized;

        previousInitialAdvancedRef.current = currentSerialized;

        // Update filter groups only if they're different
        if (!shouldSkipGroupsUpdate) {
            const mergedGroups = mergeGroupsPreservingEmptyConditions(initialAdvanced.groups, filterGroups);
            setFilterGroups(() => mergedGroups);
        }

        // Always load MULTI_SELECT data
        if (metadata?.filters) {
            const { operators, inValues, outValues, labels } = loadMultiSelectState(initialAdvanced, metadata.filters);

            if (Object.keys(operators).length > 0) {
                setMultiSelectOperator(prev => ({ ...prev, ...operators }));
            }
            if (Object.keys(inValues).length > 0 || Object.keys(outValues).length > 0) {
                setMultiSelectInValues(prev => ({ ...prev, ...inValues }));
                setMultiSelectOutValues(prev => ({ ...prev, ...outValues }));
            }
            if (Object.keys(labels).length > 0) {
                setFieldLabels(prev => {
                    const merged = { ...prev };
                    Object.keys(labels).forEach(conditionKey => {
                        const existingLabels = prev[conditionKey] || {};
                        merged[conditionKey] = { ...existingLabels, ...labels[conditionKey] };
                    });
                    return merged;
                });
            }
        }
    }, [initialAdvanced, metadata?.filters]);
};
